import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext, toIsoDate } from 'src/shared/util/secondary-page-context';

import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { MarkFiledPayload, TaxesService } from 'src/shared/services/taxes.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface MarkFiledParams {
  filingId: number;
  filingPeriod?: string;
  calculatedAmount?: number;
}

@Component({
  templateUrl: 'mark-tax-filing-filed.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MarkTaxFilingFiledPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly taxesService = inject(TaxesService);

  readonly form = this.fb.nonNullable.group({
    filed_on: [toIsoDate(new Date()), Validators.required],
    // The API takes any number, so a negative amount is stopped here.
    remitted_amount: ['', Validators.min(0)],
    confirmation_number: [''],
    filed_by: [''],
  });

  isRightSideContent = true;
  submitted = false;
  saving = false;
  errorMessage = '';
  filingId: number;
  filingPeriod = '';

  readonly boundSave = () => this.save();

  async ngOnInit() {
    const { isRightSideContent, params } = await readSecondaryPageContext<MarkFiledParams>(this.storage);
    this.isRightSideContent = isRightSideContent;
    this.filingId = this.navCtrl.getParam('filingId') ?? params.filingId;
    this.filingPeriod = this.navCtrl.getParam('filingPeriod') ?? params.filingPeriod ?? '';
    const calculatedAmount = this.navCtrl.getParam('calculatedAmount') ?? params.calculatedAmount;
    if (calculatedAmount) {
      this.form.patchValue({ remitted_amount: (calculatedAmount / 100).toFixed(2) });
    }
    this.rightSidePanelService.setDialogPageTitle('Mark Filed');
    this.cdr.markForCheck();
  }

  async save(): Promise<void> {
    this.submitted = true;
    this.errorMessage = '';
    if (this.saving || !this.form.valid) {
      this.cdr.markForCheck();
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.saving = true;
    this.cdr.markForCheck();
    try {
      await this.taxesService.markFiled(this.filingId, this.buildPayload());
      await this.storage.save('didMakePropertyTaxChanges', true);
      this.util.showSuccess('Filing marked filed.');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to mark the filing filed.';
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  // The form takes dollars; the API takes cents.
  private buildPayload(): MarkFiledPayload {
    const val = this.form.getRawValue();
    const payload: MarkFiledPayload = { filed_on: toIsoDate(val.filed_on) };
    if (val.remitted_amount !== '' && val.remitted_amount !== null) {
      payload.remitted_amount = Math.round(Number(val.remitted_amount) * 100);
    }
    if (val.confirmation_number) { payload.confirmation_number = val.confirmation_number.trim(); }
    if (val.filed_by) { payload.filed_by = val.filed_by.trim(); }
    return payload;
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
